import React, { useMemo, useState } from 'react';
import './MixedPalletBuilder.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function emptyContentLine() {
  return { cargo_id: '', quantity_pieces: '' };
}

function materialLabel(item) {
  return item.material_description || 'Material';
}

function compareItemNumbers(a, b) {
  const numA = parseFloat(a);
  const numB = parseFloat(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function sumPalletPieces(contents = []) {
  return contents.reduce((sum, line) => sum + (line.quantity_pieces || 0), 0);
}

function buildIncrementsFromLines(lines) {
  const increments = {};
  for (const line of lines) {
    const cargoId = parseInt(line.cargo_id, 10);
    const qty = parseInt(line.quantity_pieces, 10);
    if (!cargoId || !qty || qty <= 0) continue;
    increments[cargoId] = (increments[cargoId] || 0) + qty;
  }
  return increments;
}

export default function MixedPalletBuilder({
  packingListId,
  token,
  cargoItems = [],
  pallets = [],
  allocation = [],
  validationErrors = [],
  onRefresh,
  onError,
  onSuccess,
}) {
  const [itemNumber, setItemNumber] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [contentLines, setContentLines] = useState([emptyContentLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [editingPalletId, setEditingPalletId] = useState(null);
  const [editPalletForm, setEditPalletForm] = useState({ pallet_number: '', gross_weight_kg: '' });
  const [addContentForms, setAddContentForms] = useState({});

  const allocationRows = useMemo(() => {
    if (allocation.length > 0) return allocation;
    const onPallets = {};
    for (const pallet of pallets) {
      for (const content of pallet.contents || []) {
        onPallets[content.cargo_id] = (onPallets[content.cargo_id] || 0) + (content.quantity_pieces || 0);
      }
    }
    return cargoItems.map((item) => {
      const total = parseInt(item.quantity_pieces, 10) || 0;
      const assigned = onPallets[item.cargo_id] || 0;
      return {
        cargo_id: item.cargo_id,
        item_number: item.item_number,
        material_description: item.material_description,
        total_pieces: total,
        on_pallets: assigned,
        remaining: total - assigned,
        balanced: assigned === total,
      };
    });
  }, [allocation, cargoItems, pallets]);

  const allBalanced = allocationRows.length > 0 && allocationRows.every((row) => row.balanced);

  const sortedPallets = useMemo(
    () => [...pallets].sort((a, b) => compareItemNumbers(a.pallet_number, b.pallet_number)),
    [pallets]
  );

  const packingListRows = useMemo(() => {
    const rows = [];
    for (const pallet of sortedPallets) {
      for (const content of pallet.contents || []) {
        const cargo = cargoItems.find((c) => c.cargo_id === content.cargo_id);
        rows.push({
          key: `${pallet.pallet_id}-${content.pallet_content_id}`,
          item_number: pallet.pallet_number,
          material_description:
            content.cargo?.material_description ||
            cargo?.material_description ||
            `Material #${content.cargo_id}`,
          quantity_pieces: content.quantity_pieces,
        });
      }
    }
    return rows;
  }, [sortedPallets, cargoItems]);

  const getCargoById = (cargoId) => cargoItems.find((item) => item.cargo_id === cargoId);

  const validateIncrementsClient = (increments) => {
    for (const [cargoIdStr, addQty] of Object.entries(increments)) {
      const cargoId = parseInt(cargoIdStr, 10);
      const row = allocationRows.find((r) => r.cargo_id === cargoId);
      if (!row) continue;
      if (addQty > row.remaining) {
        const label = row.material_description || `Item ${row.item_number}`;
        onError(
          `${label}: cannot assign ${addQty} pieces — only ${Math.max(row.remaining, 0)} remaining of ${row.total_pieces} total`
        );
        return false;
      }
    }
    return true;
  };

  const handleContentLineChange = (index, field, value) => {
    setContentLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );
  };

  const addContentLine = () => {
    setContentLines((prev) => [...prev, emptyContentLine()]);
  };

  const removeContentLine = (index) => {
    setContentLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const apiRequest = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }
    return data;
  };

  const handleCreatePallet = async (e) => {
    e.preventDefault();
    const serial = itemNumber.trim();
    if (!serial) {
      onError('Item number is required');
      return;
    }

    const filledLines = contentLines.filter((line) => line.cargo_id && line.quantity_pieces);
    if (filledLines.length === 0) {
      onError('Add at least one material with number of pieces');
      return;
    }

    const increments = buildIncrementsFromLines(filledLines);
    if (!validateIncrementsClient(increments)) return;

    const contents = Object.entries(increments).map(([cargo_id, quantity_pieces]) => ({
      cargo_id: parseInt(cargo_id, 10),
      quantity_pieces,
    }));

    try {
      setSubmitting(true);
      await apiRequest(`${API_URL}/packing-lists/${packingListId}/pallets`, {
        method: 'POST',
        body: JSON.stringify({
          pallet_number: serial,
          gross_weight_kg: grossWeight || null,
          contents,
        }),
      });
      setItemNumber('');
      setGrossWeight('');
      setContentLines([emptyContentLine()]);
      onSuccess(`Item ${serial} added`);
      onRefresh();
    } catch (err) {
      onError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePallet = async (palletId) => {
    const pallet = pallets.find((p) => p.pallet_id === palletId);
    if (!window.confirm(`Delete item ${pallet?.pallet_number ?? palletId} and all materials on it?`)) return;
    try {
      await apiRequest(`${API_URL}/packing-lists/${packingListId}/pallets/${palletId}`, {
        method: 'DELETE',
      });
      onSuccess('Pallet deleted');
      onRefresh();
    } catch (err) {
      onError(err.message);
    }
  };

  const startEditPallet = (pallet) => {
    setEditingPalletId(pallet.pallet_id);
    setEditPalletForm({
      pallet_number: pallet.pallet_number || '',
      gross_weight_kg: pallet.gross_weight_kg ?? '',
    });
  };

  const handleSavePallet = async (palletId) => {
    try {
      await apiRequest(`${API_URL}/packing-lists/${packingListId}/pallets/${palletId}`, {
        method: 'PUT',
        body: JSON.stringify({
          pallet_number: editPalletForm.pallet_number.trim(),
          gross_weight_kg: editPalletForm.gross_weight_kg || null,
        }),
      });
      setEditingPalletId(null);
      onSuccess('Pallet updated');
      onRefresh();
    } catch (err) {
      onError(err.message);
    }
  };

  const handleDeleteContent = async (palletId, contentId) => {
    try {
      await apiRequest(
        `${API_URL}/packing-lists/${packingListId}/pallets/${palletId}/contents/${contentId}`,
        { method: 'DELETE' }
      );
      onSuccess('Material removed from pallet');
      onRefresh();
    } catch (err) {
      onError(err.message);
    }
  };

  const handleUpdateContentQty = async (palletId, contentId, cargoId, oldQty, quantity) => {
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) {
      onError('Number of pieces must be greater than 0');
      return;
    }
    const increment = qty - (parseInt(oldQty, 10) || 0);
    if (increment > 0 && !validateIncrementsClient({ [cargoId]: increment })) return;

    try {
      await apiRequest(
        `${API_URL}/packing-lists/${packingListId}/pallets/${palletId}/contents/${contentId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ quantity_pieces: qty }),
        }
      );
      onSuccess('Number of pieces updated');
      onRefresh();
    } catch (err) {
      onError(err.message);
    }
  };

  const handleAddContentToPallet = async (palletId) => {
    const form = addContentForms[palletId] || { cargo_id: '', quantity_pieces: '' };
    if (!form.cargo_id || !form.quantity_pieces) {
      onError('Select a material and enter number of pieces');
      return;
    }
    const cargoId = parseInt(form.cargo_id, 10);
    const qty = parseInt(form.quantity_pieces, 10);
    if (!validateIncrementsClient({ [cargoId]: qty })) return;

    try {
      await apiRequest(`${API_URL}/packing-lists/${packingListId}/pallets/${palletId}/contents`, {
        method: 'POST',
        body: JSON.stringify({
          cargo_id: cargoId,
          quantity_pieces: qty,
        }),
      });
      setAddContentForms((prev) => ({ ...prev, [palletId]: { cargo_id: '', quantity_pieces: '' } }));
      onSuccess('Material added to pallet');
      onRefresh();
    } catch (err) {
      onError(err.message);
    }
  };

  const setAddContentField = (palletId, field, value) => {
    setAddContentForms((prev) => ({
      ...prev,
      [palletId]: { ...(prev[palletId] || { cargo_id: '', quantity_pieces: '' }), [field]: value },
    }));
  };

  const renderMaterialDescription = (content) => {
    const cargo =
      getCargoById(content.cargo_id) ||
      (content.cargo?.material_description
        ? { item_number: content.cargo.item_number, material_description: content.cargo.material_description }
        : null);
    if (!cargo) return `Material #${content.cargo_id}`;
    return materialLabel(cargo);
  };

  return (
    <div className="mixed-pallet-builder">
      <h2>Packing list detail ({sortedPallets.length} item numbers)</h2>
      <p className="section-hint">
        Recreate the supplier packing list: each <strong>item number</strong> is one pallet.
        Under that item number, list every <strong>material description</strong> and <strong>number of pieces</strong> on that pallet
        (e.g. Item 1 → 72 pcs of one material; Item 2 → 3 + 14 + 19 pcs across three materials).
        Piece counts per material must add up to the totals entered above.
      </p>

      {cargoItems.length > 0 && (
        <div className="allocation-summary">
          <h3>Material totals (shipment summary)</h3>
          <table className="allocation-table">
            <thead>
              <tr>
                <th>Material description</th>
                <th>Total pieces</th>
                <th>On item numbers</th>
                <th>Remaining</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {allocationRows.map((row) => (
                <tr key={row.cargo_id} className={row.balanced ? 'row-balanced' : row.on_pallets > row.total_pieces ? 'row-over' : 'row-pending'}>
                  <td>{row.material_description}</td>
                  <td>{row.total_pieces}</td>
                  <td>{row.on_pallets}</td>
                  <td>{row.remaining}</td>
                  <td>
                    {row.balanced ? (
                      <span className="status-ok">Balanced</span>
                    ) : row.on_pallets > row.total_pieces ? (
                      <span className="status-over">Over by {row.on_pallets - row.total_pieces}</span>
                    ) : (
                      <span className="status-pending">{row.remaining} to assign</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {packingListRows.length > 0 && (
        <div className="packing-list-detail">
          <h3>Supplier packing list (by item number)</h3>
          <table className="packing-list-table">
            <thead>
              <tr>
                <th>Item #</th>
                <th>Material description</th>
                <th>Number of pieces</th>
              </tr>
            </thead>
            <tbody>
              {packingListRows.map((row) => (
                <tr key={row.key}>
                  <td className="item-number-cell">{row.item_number}</td>
                  <td>{row.material_description}</td>
                  <td>{row.quantity_pieces}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {allBalanced && allocationRows.length > 0 && (
        <div className="alert alert-success-inline">
          All materials are fully assigned — packing list totals match.
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="alert alert-warning">
          <strong>Piece counts do not match totals:</strong>
          <ul>
            {validationErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {cargoItems.length === 0 && (
        <div className="builder-notice">Add material lines above (description + total pieces) before entering item numbers.</div>
      )}

      {sortedPallets.length === 0 ? (
        <div className="no-pallets">No item numbers entered yet — add the first row from the supplier packing list below.</div>
      ) : (
        <div className="pallet-cards">
          <h3 className="edit-section-title">Edit by item number</h3>
          {sortedPallets.map((pallet) => (
            <div key={pallet.pallet_id} className="pallet-card">
              <div className="pallet-card-header">
                {editingPalletId === pallet.pallet_id ? (
                  <div className="pallet-edit-fields">
                    <input
                      type="text"
                      value={editPalletForm.pallet_number}
                      onChange={(e) =>
                        setEditPalletForm((prev) => ({ ...prev, pallet_number: e.target.value }))
                      }
                      placeholder="Item number"
                      aria-label="Item number"
                    />
                    <input
                      type="number"
                      value={editPalletForm.gross_weight_kg}
                      onChange={(e) =>
                        setEditPalletForm((prev) => ({ ...prev, gross_weight_kg: e.target.value }))
                      }
                      placeholder="Gross kg (optional)"
                      aria-label="Gross weight"
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSavePallet(pallet.pallet_id)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditingPalletId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <strong>Item No. {pallet.pallet_number}</strong>
                      {pallet.gross_weight_kg != null && (
                        <span className="pallet-weight"> — {pallet.gross_weight_kg} kg gross</span>
                      )}
                    </div>
                    <div className="pallet-card-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => startEditPallet(pallet)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeletePallet(pallet.pallet_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>

              <table className="pallet-contents-table">
                <thead>
                  <tr>
                    <th>Material description</th>
                    <th>Number of pieces</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(pallet.contents || []).map((content) => (
                    <tr key={content.pallet_content_id}>
                      <td>{renderMaterialDescription(content)}</td>
                      <td>
                        <input
                          type="number"
                          className="qty-input"
                          defaultValue={content.quantity_pieces}
                          min="1"
                          onBlur={(e) => {
                            if (String(e.target.value) !== String(content.quantity_pieces)) {
                              handleUpdateContentQty(
                                pallet.pallet_id,
                                content.pallet_content_id,
                                content.cargo_id,
                                content.quantity_pieces,
                                e.target.value
                              );
                            }
                          }}
                          aria-label="Number of pieces"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDeleteContent(pallet.pallet_id, content.pallet_content_id)
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total on item {pallet.pallet_number}</strong></td>
                    <td colSpan="2"><strong>{sumPalletPieces(pallet.contents)} pieces</strong></td>
                  </tr>
                </tfoot>
              </table>

              <div className="add-content-row">
                <select
                  value={(addContentForms[pallet.pallet_id] || {}).cargo_id || ''}
                  onChange={(e) => setAddContentField(pallet.pallet_id, 'cargo_id', e.target.value)}
                  aria-label="Material description"
                >
                  <option value="">Select material…</option>
                  {cargoItems.map((item) => (
                    <option key={item.cargo_id} value={item.cargo_id}>
                      {materialLabel(item)} ({item.quantity_pieces ?? 0} total)
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  placeholder="Number of pieces"
                  value={(addContentForms[pallet.pallet_id] || {}).quantity_pieces || ''}
                  onChange={(e) =>
                    setAddContentField(pallet.pallet_id, 'quantity_pieces', e.target.value)
                  }
                  aria-label="Number of pieces to add"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleAddContentToPallet(pallet.pallet_id)}
                >
                  Add material
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cargoItems.length > 0 && (
        <form className="new-pallet-form" onSubmit={handleCreatePallet}>
          <h3>Add item number from supplier packing list</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Item number *</label>
              <input
                type="text"
                value={itemNumber}
                onChange={(e) => setItemNumber(e.target.value)}
                placeholder="e.g., 1"
                required
              />
              <small className="field-hint-inline">Identifies this pallet on the supplier packing list</small>
            </div>
            <div className="form-group">
              <label>Gross weight (kg)</label>
              <input
                type="number"
                value={grossWeight}
                onChange={(e) => setGrossWeight(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="content-lines">
            <label>Materials on item {itemNumber || '…'} *</label>
            <div className="content-lines-header">
              <span>Material description</span>
              <span>Number of pieces</span>
              <span />
            </div>
            {contentLines.map((line, index) => (
              <div key={index} className="content-line-row">
                <select
                  value={line.cargo_id}
                  onChange={(e) => handleContentLineChange(index, 'cargo_id', e.target.value)}
                  required={index === 0}
                  aria-label={`Material description ${index + 1}`}
                >
                  <option value="">Select material…</option>
                  {cargoItems.map((item) => {
                    const row = allocationRows.find((r) => r.cargo_id === item.cargo_id);
                    return (
                      <option key={item.cargo_id} value={item.cargo_id}>
                        {materialLabel(item)}
                        {row ? ` — ${row.remaining} remaining of ${row.total_pieces}` : ''}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="number"
                  min="1"
                  placeholder="Number of pieces"
                  value={line.quantity_pieces}
                  onChange={(e) => handleContentLineChange(index, 'quantity_pieces', e.target.value)}
                  aria-label={`Number of pieces ${index + 1}`}
                />
                {contentLines.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => removeContentLine(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addContentLine}>
              + Add another material on this item number
            </button>
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Add item number'}
          </button>
        </form>
      )}
    </div>
  );
}
